import React, { useCallback, useState } from 'react';
import { compose } from 'redux';
import { Alert, View, RefreshControl } from 'react-native';
import { useNavigation } from '@react-navigation/native';
import { KeyboardAwareScrollView } from 'react-native-keyboard-aware-scroll-view';

import { DetailContainer } from '../../containers';
import { useRefreshPage } from '../../hooks/useRefreshPage';
import { useThemedStyles } from '../../hooks/useThemedStyles';
import { Button, Dialog, Text, TypographyHeading } from '../../components';
import { Card, SectionTitle } from '../../design/components';
import PageHeading from '../../design/components/PageHeading';
import { TextInputEditInPlace } from '../../components/input';
import { useUserContext } from '../../../common/utils/context';
import { POS_TYPE, ROLE_PERMISSIONS, TENANT_CONSTANT_TYPE } from '../../../common/constants';
import { TenantConstantSelector, RouteSelector, PlaceSelector, WarehouseSelector } from '../../components/selector';
import { getFullMachineName } from '../../../common/utils/machine';
import getPOSModelName from '../../../common/utils/machine/getPOSModelName';

const MachineDetail = ({
	refetch,
	data: machine,
	errors,
	modifyItem: modifyMachine,
}) => {

	const [refreshPage, isRefreshing] = useRefreshPage(refetch);
	const [isMoveDialogOpen, setIsMoveDialogOpen] = useState(false);

	const navigation = useNavigation();
	const { hasPermission, isDriver } = useUserContext();
	const canEditMachine = hasPermission(ROLE_PERMISSIONS.ADD_EDIT_MACHINES);

	const onChangeMachine = useCallback(field => value => modifyMachine({ [field]: value ? value.id : null }), [modifyMachine]);
	const closeMoveDialog = useCallback(() => setIsMoveDialogOpen(false), []);
	const openMoveDialog = useCallback(() => setIsMoveDialogOpen(true), []);
	const openLocationSetupCreator = useCallback(() => {
		closeMoveDialog();
		navigation.navigate('LocationSetupCreatorView');
	}, [closeMoveDialog, navigation]);

	const moveToWarehouse = useCallback(async (warehouse, place) => {
		const nextLocationId = warehouse?.location?.id;
		const nextPlaceId = place?.id;
		if (!nextLocationId || !nextPlaceId || nextLocationId === machine.location?.id) {
			return;
		}

		await modifyMachine({ location: nextLocationId, place: nextPlaceId });
		closeMoveDialog();
	}, [closeMoveDialog, machine.location?.id, modifyMachine]);

	const isMarket = machine.type === POS_TYPE.MARKET;
	const posModelName = getPOSModelName(machine, true);
	const moveButtonLabel = isMarket ? 'Move market to warehouse' : 'Move machine';
	const canMovePOS = canEditMachine && !isDriver;
	const styles = useThemedStyles(createStyles);

	return (
		<KeyboardAwareScrollView
			style={styles.scrollView}
			contentContainerStyle={styles.scrollContent}
			scrollIndicatorInsets={{ right: 1 }}
			refreshControl={(
				<RefreshControl
					refreshing={isRefreshing}
					onRefresh={refreshPage}
				/>
			)}
		>
			<PageHeading
				title={getFullMachineName(machine, machine.place, machine.location)}
			/>
			<Card style={styles.section}>
				<SectionTitle title="Identification" />
				<TypographyHeading
					fieldLabel={true}
					label="ID"
				/>
				<TextInputEditInPlace
					value={machine.assetId}
					placeholder="No ID"
					field="assetId"
					disabled={true}
					onConfirm={modifyMachine}
					error={errors.assetId}
				/>

				{
					!isMarket && (
						<>
							<TypographyHeading
								fieldLabel={true}
								label="Serial number"
							/>
							<TextInputEditInPlace
								value={machine.serialNumber}
								placeholder="No serial number"
								field="serialNumber"
								disabled={!canEditMachine}
								onConfirm={modifyMachine}
							/>
						</>
					)
				}
			</Card>
			{
				!isMarket && (
					<Card style={styles.section}>
						<SectionTitle title="Machine details" />

						<TenantConstantField
							machine={machine}
							onChangeMachine={onChangeMachine}
							canEditMachine={canEditMachine}
							label="Key"
							field="machineKey"
							constantType={TENANT_CONSTANT_TYPE.MACHINE_KEY}
						/>
						<TenantConstantField
							machine={machine}
							onChangeMachine={onChangeMachine}
							canEditMachine={canEditMachine}
							label="Manufacturer"
							field="manufacturer"
							constantType={TENANT_CONSTANT_TYPE.MACHINE_MANUFACTURER}
						/>
						<TenantConstantField
							machine={machine}
							onChangeMachine={onChangeMachine}
							canEditMachine={canEditMachine}
							label="Distributor"
							field="distributor"
							constantType={TENANT_CONSTANT_TYPE.MACHINE_DISTRIBUTOR}
						/>
						<TenantConstantField
							machine={machine}
							onChangeMachine={onChangeMachine}
							canEditMachine={canEditMachine}
							label="Machine model"
							field="machineModel"
							constantType={TENANT_CONSTANT_TYPE.MACHINE_MODEL}
						/>
						<TenantConstantField
							machine={machine}
							onChangeMachine={onChangeMachine}
							canEditMachine={canEditMachine}
							label="Machine type"
							field="machineType"
							constantType={TENANT_CONSTANT_TYPE.MACHINE_TYPE}
						/>
						<TenantConstantField
							machine={machine}
							onChangeMachine={onChangeMachine}
							canEditMachine={canEditMachine}
							label="Machine wrap"
							field="wrap"
							constantType={TENANT_CONSTANT_TYPE.MACHINE_WRAP}
						/>
						<TenantConstantField
							machine={machine}
							onChangeMachine={onChangeMachine}
							canEditMachine={canEditMachine}
							label="Aerial position"
							field="aerialPosition"
							constantType={TENANT_CONSTANT_TYPE.MACHINE_AERIAL_POSITION}
						/>
					</Card>
				)
			}

			{/* <TypographyHeading
				fieldLabel={true}
				label={isMarket ? 'KIOSK' : 'MODEM'}
			/>
			<CommDeviceSelector
				initialValue={machine.commDevice}
				onChange={onChangeMachine('commDevice')}
				disabled={!canEditMachine}
			/> */}

			{
				!isMarket && (
					<Card style={styles.section}>
						<SectionTitle title="Peripherals" />

						<TenantConstantField
							machine={machine}
							onChangeMachine={onChangeMachine}
							canEditMachine={canEditMachine}
							label="Coin mech"
							field="coinMech"
							constantType={TENANT_CONSTANT_TYPE.MACHINE_COIN_MECH}
						/>
						<TenantConstantField
							machine={machine}
							onChangeMachine={onChangeMachine}
							canEditMachine={canEditMachine}
							label="Note reader"
							field="noteReader"
							constantType={TENANT_CONSTANT_TYPE.MACHINE_NOTE_READER}
						/>
						<TenantConstantField
							machine={machine}
							onChangeMachine={onChangeMachine}
							canEditMachine={canEditMachine}
							label="Control board"
							field="motherboard"
							constantType={TENANT_CONSTANT_TYPE.MACHINE_MOTHERBOARD}
						/>
					</Card>
				)
			}

			<Card style={styles.section}>
				<SectionTitle title="Route" />

				<RouteSelector
					initialValue={machine.route}
					onChange={onChangeMachine('route')}
					disabled={!canEditMachine || isDriver}
				/>
			</Card>

			<Card style={styles.section}>
				<SectionTitle title="Location" />

				<Text style={styles.readOnlyValue}>
					{machine.location?.name || 'No location'}
				</Text>
				{canMovePOS && (
					<>
						<Button
							label={moveButtonLabel}
							onPress={openMoveDialog}
							style={styles.moveButton}
						/>
						<Dialog
							isOpen={isMoveDialogOpen}
							onClose={closeMoveDialog}
						>
							<MovePOSDialog
								machine={machine}
								moveToWarehouse={moveToWarehouse}
								onCreateLocationSetup={openLocationSetupCreator}
								posModelName={posModelName}
								isMarket={isMarket}
							/>
						</Dialog>
					</>
				)}

				<TypographyHeading
					fieldLabel={true}
					label="Place"
				/>
				<PlaceSelector
					initialValue={machine.place}
					onChange={onChangeMachine('place')}
					location={machine.location}
					initialFilter={machine.location?.id ? { location: machine.location.id } : undefined}
					disabled={!machine.location || !canEditMachine}
				/>
			</Card>
		</KeyboardAwareScrollView>
	);
};

const createStyles = ({ ui: { colors, type, space, layout } }) => ({
	scrollView: {
		flex: 1,
	},
	scrollContent: {
		width: '100%',
		maxWidth: layout.maxContentWidth,
		alignSelf: 'center',
		paddingHorizontal: space.gutter,
		paddingTop: 20,
		paddingBottom: space.xl,
		gap: space.gutter,
	},
	section: {
		gap: space.xs,
	},
	readOnlyValue: {
		...type.rowText,
		color: colors.text,
		marginBottom: space.sm,
	},
	moveButton: {
		alignSelf: 'flex-start',
		marginBottom: space.sm,
	},
	dialogTitle: {
		...type.cardTitle,
		color: colors.text,
		marginBottom: space.md,
		paddingRight: 28,
	},
	dialogText: {
		...type.bodySmall,
		color: colors.textSecondary,
		marginBottom: space.md,
	},
	dialogSection: {
		marginTop: space.sm,
	},
	dialogActions: {
		flexDirection: 'row',
		flexWrap: 'wrap',
		gap: space.sm,
		marginTop: space.md,
	},
	dialogActionButton: {
		marginBottom: space.sm,
	},
	selectorButton: {
		marginTop: space.xs,
		marginBottom: space.xs,
	},
});

const MovePOSDialog = ({
	isMarket,
	machine,
	moveToWarehouse,
	onCreateLocationSetup,
	posModelName,
}) => {
	const styles = useThemedStyles(createStyles);
	const [moveType, setMoveType] = useState(isMarket ? 'warehouse' : 'setup');
	const [selectedWarehouse, setSelectedWarehouse] = useState(null);
	const [selectedPlace, setSelectedPlace] = useState(null);
	const [isSubmitting, setIsSubmitting] = useState(false);
	const selectedLocationId = selectedWarehouse?.location?.id || null;
	const selectedCurrentLocation = Boolean(selectedLocationId && selectedLocationId === machine.location?.id);
	const canSubmitWarehouseMove = Boolean(selectedLocationId && selectedPlace?.id && !selectedCurrentLocation);

	const submitWarehouseMove = useCallback(async () => {
		if (!canSubmitWarehouseMove) {
			return;
		}

		try {
			setIsSubmitting(true);
			await moveToWarehouse(selectedWarehouse, selectedPlace);
		}
		catch (error) {
			Alert.alert('Unable to move', 'Something went wrong while moving this POS.');
		}
		finally {
			setIsSubmitting(false);
		}
	}, [canSubmitWarehouseMove, moveToWarehouse, selectedPlace, selectedWarehouse]);

	return (
		<View>
			<Text style={styles.dialogTitle}>Move {posModelName}</Text>
			{!isMarket && (
				<>
					<Text style={styles.dialogText}>Choose whether this move should go through setup or directly to a warehouse.</Text>
					<View style={styles.dialogActions}>
						<Button
							label="Client/site setup"
							onPress={() => setMoveType('setup')}
							secondary={moveType === 'setup'}
							style={styles.dialogActionButton}
						/>
						<Button
							label="Warehouse"
							onPress={() => setMoveType('warehouse')}
							secondary={moveType === 'warehouse'}
							style={styles.dialogActionButton}
						/>
					</View>
				</>
			)}
			{!isMarket && moveType === 'setup' && (
				<View style={styles.dialogSection}>
					<Text style={styles.dialogText}>Client and site moves are completed through setup so setup QA and place selection are completed.</Text>
					<Button
						label="Create location setup"
						onPress={onCreateLocationSetup}
						secondary={true}
					/>
				</View>
			)}
			{moveType === 'warehouse' && (
				<View style={styles.dialogSection}>
					<Text style={styles.dialogText}>Moving to a warehouse will remove this POS from any route.</Text>
					<WarehouseSelector
						value={selectedWarehouse}
						onChange={warehouse => {
							setSelectedWarehouse(warehouse || null);
							setSelectedPlace(null);
						}}
						buttonStyle={styles.dialogActionButton}
					/>
					{selectedLocationId && (
						<>
							<Text style={styles.dialogText}>Select the Place at this warehouse.</Text>
							<PlaceSelector
								key={`warehouse_place_${selectedLocationId}`}
								value={selectedPlace}
								onChange={place => setSelectedPlace(place || null)}
								initialFilter={{ location: selectedLocationId }}
								buttonStyle={styles.dialogActionButton}
							/>
						</>
					)}
					{selectedCurrentLocation && (
						<Text style={styles.dialogText}>This POS is already at the selected warehouse.</Text>
					)}
					<Button
						label="Move to warehouse"
						onPress={submitWarehouseMove}
						disabled={!canSubmitWarehouseMove || isSubmitting}
						loading={isSubmitting}
						secondary={true}
					/>
				</View>
			)}
		</View>
	);
};

const TenantConstantField = ({
	machine,
	onChangeMachine,
	canEditMachine,
	label,
	field,
	constantType,
}) => {
	const styles = useThemedStyles(createStyles);

	return (
		<>
			<TypographyHeading
				fieldLabel={true}
				label={label}
			/>
			<TenantConstantSelector
				constantType={constantType}
				initialValue={machine[field]}
				onChange={onChangeMachine(field)}
				disabled={!canEditMachine}
				buttonStyle={styles.selectorButton}
			/>
		</>
	);
};

export default compose(DetailContainer)(MachineDetail);
